'use client';

import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useMotionValueEvent, useScroll, useReducedMotion } from 'motion/react';
import Logo from '@/components/Logo';
import useAuth from '@/lib/firebase/auth/store';

interface HeaderProps {
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative';
  children: React.ReactNode;
  subheader?: React.ReactNode;
  demoMode?: boolean;
  isMarketingWebsite?: boolean;
  backButton?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isWorkflow?: boolean;
  showCredits?: boolean;
  isConfigurator?: boolean;
}

const TOOLBAR_PADDING = {
  xs: 1,
  md: 3,
} as const;

// Top toolbar height — must match MenuToolbar's dropdown `top` offset so the
// menu bar sits flush under the top bar.
const HEADER_HEIGHT = 64;

const HeaderBase = ({
  position = 'sticky',
  children,
  subheader,
  demoMode,
  isMarketingWebsite = false,
  backButton = false,
  onMouseEnter,
  onMouseLeave,
  isWorkflow,
  showCredits,
  isConfigurator,
}: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [childrenVisible, setChildrenVisible] = useState(false);
  const { scrollY } = useScroll();
  const reduced = useReducedMotion();
  const { userData } = useAuth();
  const customCompanyLogo = !!userData?.company?.logo && !demoMode;

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled(latest > 8);
  });

  const showScrollEffect = isMarketingWebsite && scrolled;

  const handleMouseEnter = () => {
    setChildrenVisible(true);
    onMouseEnter?.();
  };

  const handleMouseLeave = () => {
    setChildrenVisible(false);
    onMouseLeave?.();
  };

  return (
    <>
      <AppBar
        position={position}
        elevation={3}
        onMouseEnter={isMarketingWebsite ? handleMouseEnter : undefined}
        onMouseLeave={isMarketingWebsite ? handleMouseLeave : undefined}
        sx={{
          // Single source of truth for the animated background — the Toolbar
          // below no longer sets its own backgroundColor, so there's only one
          // element transitioning and nothing to desync.
          backgroundColor: childrenVisible
            ? 'white'
            : showScrollEffect
              ? 'rgba(255, 255, 255, 0.85)'
              : 'background.default',
          color: 'primary.main',
          // Platform header: no drop shadow — the elevation-3 shadow bled over the
          // sticky grey sub-header and the slide-down dropdown. A hairline border
          // keeps separation without casting onto the content below.
          boxShadow: isMarketingWebsite
            ? showScrollEffect && !childrenVisible
              ? '0 1px 0 rgba(0, 0, 0, 0.06)'
              : 'none'
            : subheader
              ? 'none'
              : 'gba(0, 0, 0, 0.05) 0px 3px',
          borderBottom: isMarketingWebsite ? 0 : '1px solid',
          borderColor: isMarketingWebsite ? 'divider' : 'transparent',
          backdropFilter: showScrollEffect ? 'blur(12px)' : 'none',
          WebkitBackdropFilter: showScrollEffect ? 'blur(12px)' : 'none',
          transition: reduced
            ? 'none'
            : 'background-color 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <Toolbar
          variant='regular'
          sx={
            isMarketingWebsite
              ? {
                  px: TOOLBAR_PADDING,
                  py: 0,
                  pb: { xs: 0, md: 0.5 },
                  pt: 1,
                  maxWidth: 'lg',
                  mx: 'auto',
                  width: '100%',
                }
              : {
                  px: TOOLBAR_PADDING,
                }
          }
        >
          <Box
            sx={{
              flexGrow: 1,
              '& > div': {
                width: 'fit-content',
              },
            }}
          >
            <Box
              sx={{
                display: {
                  xs: 'block',
                  sm: 'none',
                },
              }}
            >
              <Logo
                height={customCompanyLogo ? 40 : 21}
                demoMode={demoMode}
                backButton={backButton}
              />
            </Box>

            <Box
              sx={{
                display: {
                  xs: 'none',
                  sm: 'block',
                },
              }}
            >
              <Logo
                height={customCompanyLogo ? 50 : 32}
                demoMode={demoMode}
                backButton={backButton}
              />
            </Box>
          </Box>

          <Stack
            direction='row'
            spacing={0.75}
            sx={{
              // MUI spacing rejects `false`, so the no-margin case must be undefined.
              mr:
                isMarketingWebsite || isConfigurator
                  ? undefined
                  : {
                      xs: 4,
                      md: !isWorkflow ? 4 : 0,
                      xxl: !isWorkflow ? 7 : 0,
                    },
              '& > :last-child': {
                ml: showCredits ? 1.5 : 0.75,
              },
            }}
          >
            {children}
          </Stack>
        </Toolbar>
      </AppBar>

      {subheader && (
        <Box
          component='section'
          sx={{
            // Always sticky so the menu bar pins under the top bar and the z-index
            // below applies (z-index only takes effect on a positioned element).
            position: 'sticky',
            top: HEADER_HEIGHT,
            left: 0,
            right: 0,
            // One stacking layer below the dropdown's blur backdrop (appBar - 2) so an
            // open header dropdown slides OVER the menu bar, while the top bar (appBar)
            // stays above the dropdown and it still emerges from under the top bar.
            zIndex: (theme) => theme.zIndex.appBar - 3,
            boxShadow: 3,
          }}
        >
          {subheader}
        </Box>
      )}
    </>
  );
};

export default HeaderBase;
